import { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import {
  buildHitLabLoginPath,
  buildHitLabIntakeReturnTo,
  isHitLabClientEnabled,
} from './hitLabUtils';
import { HITLAB_FUNNEL_EVENTS, recordHitLabEvent } from './hitLabTelemetry';
import './hitLab.css';

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/thejoegalaxy',
  },
  {
    label: 'X',
    href: 'https://x.com/joegalaxy1',
  },
];

const HITLAB_MEDIA = {
  hero: {
    src:
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_880,h_1040/v1785821149/IMG_0088_lrcm1b.jpg',
    srcSet: [
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_520,h_680/v1785821149/IMG_0088_lrcm1b.jpg 520w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_880,h_1040/v1785821149/IMG_0088_lrcm1b.jpg 880w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_1160,h_1320/v1785821149/IMG_0088_lrcm1b.jpg 1160w',
    ].join(', '),
    sizes: '(max-width: 860px) calc(100vw - 32px), 38vw',
    alt: 'Joe Galaxy performing onstage at SunFest in front of a crowd.',
  },
  backstage: {
    src:
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_560/v1785821197/IMG_0209_rvufhz.jpg',
    srcSet: [
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_360/v1785821197/IMG_0209_rvufhz.jpg 360w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_560/v1785821197/IMG_0209_rvufhz.jpg 560w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_760/v1785821197/IMG_0209_rvufhz.jpg 760w',
    ].join(', '),
    sizes: '(max-width: 860px) calc(100vw - 32px), 26vw',
    alt: 'Joe Galaxy backstage at SunFest with Flo Rida after performing.',
  },
};

export default function HitLabLanding() {
  const { player } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const enabled = isHitLabClientEnabled();

  useEffect(() => {
    if (!enabled) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.LANDING_VIEWED, {
      search: location.search,
      path: '/hitlab',
    });
  }, [enabled, location.search]);

  const startIntake = () => {
    if (!enabled) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.START_ARTIST_INTAKE_CLICKED, {
      search: location.search,
      path: '/hitlab',
      dedupe: false,
    });
    if (player?.token) {
      history.push(buildHitLabIntakeReturnTo(location.search));
      return;
    }
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.AUTH_STARTED, {
      search: location.search,
      path: '/hitlab',
      dedupe: false,
    });
    history.push(buildHitLabLoginPath(location.search));
  };

  const ctaLabel = enabled ? 'Start Artist Intake' : 'Artist intake opens soon';

  return (
    <main className="hitlab-page hitlab-landing">
      <Helmet>
        <title>JG HitLab Artist Intake | Joe Galaxy</title>
        <meta
          name="description"
          content="Submit one original record for the Joe Galaxy curated artist intake."
        />
      </Helmet>

      <section className="hitlab-hero">
        <div className="hitlab-shell hitlab-hero-grid">
          <div className="hitlab-hero-copy">
            <p className="hitlab-kicker">JG HITLAB ARTIST INTAKE</p>
            <h1>Submit the Record You'd Bet On</h1>
            <p className="hitlab-pattern">
              Most artists send too many songs. Send one.
            </p>
            <p className="hitlab-lede">
              Not your catalog. Not ten links. One original record.
            </p>
            <p>
              Joe Galaxy is opening a curated door for serious artists with
              serious records. Create your JG HitLab account, start your
              HitLab artist profile, and put your strongest song into the
              intake.
            </p>
            <ul className="hitlab-trust-chips" aria-label="HitLab intake boundaries">
              <li>Original music only</li>
              <li>Rights must be clear</li>
              <li>Placement cannot be bought</li>
            </ul>
            <div className="hitlab-cta-row">
              <button
                type="button"
                className="hitlab-primary-btn"
                onClick={startIntake}
                disabled={!enabled}
              >
                {ctaLabel}
              </button>
              <a className="hitlab-secondary-link" href="#hitlab-qualifies">
                See What Qualifies
              </a>
            </div>
          </div>
          <aside
            className="hitlab-hero-card hitlab-hero-media-card"
            aria-label="Joe Galaxy performance credibility"
          >
            <figure className="hitlab-hero-media">
              <div className="hitlab-hero-media-frame">
                <img
                  src={HITLAB_MEDIA.hero.src}
                  srcSet={HITLAB_MEDIA.hero.srcSet}
                  sizes={HITLAB_MEDIA.hero.sizes}
                  alt={HITLAB_MEDIA.hero.alt}
                  width="880"
                  height="1040"
                  decoding="async"
                />
              </div>
              <figcaption>
                <span>Performed on the Ford Stage at SunFest</span>
                <h2>Real stage history. Serious curation.</h2>
                <small>
                  One song enters the review queue. Quality, rights clarity,
                  and curator decision set the next step.
                </small>
              </figcaption>
            </figure>
          </aside>
        </div>
      </section>

      <section
        id="hitlab-qualifies"
        className="hitlab-shell hitlab-proof-grid"
        aria-label="What qualifies for JG HitLab"
      >
        <article className="hitlab-wide-card">
          <span>THE FILTER</span>
          <h2>This is for one serious record.</h2>
          <p>
            Most artists do not need another random submission link. They need
            a real door, a real curator, and a clean way to put their strongest
            record in front of the Joe Galaxy intake process.
          </p>
        </article>
        <article>
          <span>ONE SHOT</span>
          <h3>One artist profile</h3>
          <p>One original record, one clean submission path, no catalog dump.</p>
        </article>
        <article>
          <span>BOUNDARY</span>
          <h3>No shortcuts</h3>
          <p>
            No covers, uncleared samples, unclear beat rights, or unresolved
            producer approvals.
          </p>
        </article>
      </section>

      <section
        className="hitlab-shell hitlab-performance-proof"
        aria-label="Joe Galaxy real performance history"
      >
        <div className="hitlab-performance-proof-copy">
          <p className="hitlab-kicker">REAL PERFORMANCE HISTORY</p>
          <h2>Real performance history behind the intake.</h2>
          <p>
            Joe Galaxy is opening a curated door for serious artists with
            serious records.
          </p>
        </div>
        <article className="hitlab-proof-media-card">
          <div className="hitlab-proof-media-frame">
            <img
              src={HITLAB_MEDIA.backstage.src}
              srcSet={HITLAB_MEDIA.backstage.srcSet}
              sizes={HITLAB_MEDIA.backstage.sizes}
              alt={HITLAB_MEDIA.backstage.alt}
              width="520"
              height="420"
              loading="lazy"
              decoding="async"
            />
          </div>
          <span>REAL PERFORMANCE HISTORY</span>
          <h3>Backstage at SunFest</h3>
          <p>Real performance history behind the intake.</p>
        </article>
      </section>

      <section className="hitlab-band">
        <div className="hitlab-shell hitlab-opportunity">
          <div>
            <p className="hitlab-kicker">WHY ONE RECORD?</p>
            <h2>Why One Record?</h2>
            <p>
              Because curation rewards focus. If you can't choose the record
              you would bet on, neither can the listener.
            </p>
          </div>
          <div className="hitlab-path-list">
            <article>
              <strong>One artist profile</strong>
              <span>One clean identity behind the record.</span>
            </article>
            <article>
              <strong>One original record</strong>
              <span>Your strongest song gets the clearest listen.</span>
            </article>
            <article>
              <strong>No catalog dumps</strong>
              <span>The intake is built for focus, not volume.</span>
            </article>
            <article>
              <strong>Placement cannot be bought</strong>
              <span>The song has to earn its next step.</span>
            </article>
          </div>
        </div>
      </section>

      <section className="hitlab-shell hitlab-gate">
        <div>
          <p className="hitlab-kicker">THIS IS NOT</p>
          <h2>Not Another Fake Music Opportunity</h2>
          <p>
            No label bait. No bought placement. No playlist promise. One
            focused door for one serious original record.
          </p>
        </div>
        <ul>
          <li>Not bought placement</li>
          <li>Not a record-deal promise</li>
          <li>Not a playlist guarantee</li>
          <li>Not a place to dump your whole catalog</li>
        </ul>
      </section>

      <section className="hitlab-shell hitlab-gate hitlab-paths-section">
        <div>
          <p className="hitlab-kicker">NEXT STEPS</p>
          <h2>Strong Records Move Forward</h2>
          <p>
            The intake starts with one record. The next step is earned by the
            song, the rights, and Joe Galaxy's curator decision.
          </p>
          <p className="hitlab-hero-trust">
            Not every record moves forward. Placement cannot be bought.
          </p>
        </div>
        <div className="hitlab-path-list">
          <article>
            <strong>JG HitLab</strong>
            <span>
              The intake lane for serious records: artist profile, rights check,
              curator review, and next-step decision.
            </span>
          </article>
          <article>
            <strong>Joe Galaxy Hit Radar</strong>
            <span>
              Joe Galaxy's shortlist for records with standout hook, identity,
              replay value, and market energy.
            </span>
          </article>
          <article>
            <strong>Joe Galaxy Radio Rotation</strong>
            <span>Selected rights-clean records enter Joe Galaxy Radio Rotation.</span>
          </article>
          <article>
            <strong>LocalOps Street Promo</strong>
            <span>
              Region-ready records become candidates for proof-backed local
              street campaigns powered by JG LocalOps helpers.
            </span>
          </article>
        </div>
      </section>

      <section className="hitlab-shell hitlab-final-cta">
        <div>
          <p className="hitlab-kicker">ONE RECORD</p>
          <h2>Bring the song you would bet on.</h2>
          <p>Start your JG artist intake.</p>
        </div>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={startIntake}
          disabled={!enabled}
        >
          {ctaLabel}
        </button>
        <nav className="hitlab-socials" aria-label="Joe Galaxy social links">
          {SOCIAL_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.label}
            </a>
          ))}
        </nav>
      </section>
    </main>
  );
}
